import { Decimal } from 'decimal.js';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { BulkProposalCard } from './BulkProposalCard';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import type { MoveInvoicesPayload } from './proposal-part.types';

interface Props {
  proposalId: string;
  payload: MoveInvoicesPayload;
  resolved?: string | undefined;
  onConfirm: (proposalId: string) => void;
  onCancel: (proposalId: string) => void;
}

/** Per-currency totals — money is never summed across currencies, nor in floating point. */
function useTotals(rows: MoveInvoicesPayload['invoices']) {
  const formatAmount = useFormatCurrency();
  const totals = new Map<string, Decimal>();

  for (const row of rows) {
    if (row.amount === null || row.currency === null) continue;

    let amount: Decimal;
    try {
      amount = new Decimal(row.amount);
    } catch {
      continue;
    }
    if (!amount.isFinite()) continue;

    totals.set(row.currency, (totals.get(row.currency) ?? new Decimal(0)).plus(amount));
  }

  return [...totals.entries()].map(([currency, total]) => ({
    currency,
    label: formatAmount(total.toString(), currency)
  }));
}

/**
 * Confirmation card for a bulk approval-status change. The target status is named in the
 * title and every affected invoice is listed with its amount, so the user sees exactly
 * what they are about to approve, reject or move.
 */
export function MoveInvoicesStatusProposalCard({
  proposalId,
  payload,
  resolved,
  onConfirm,
  onCancel
}: Props) {
  const t = useFeatureTranslation('aiChat');
  const tApprovalStatus = useEnumTranslation('approvalStatus');
  const formatAmount = useFormatCurrency();
  const totals = useTotals(payload.invoices);
  const status = payload.targetApprovalStatus ? tApprovalStatus(payload.targetApprovalStatus) : '';

  return (
    <BulkProposalCard
      title={t('proposals.move-invoices-status.title', { status })}
      description={t('proposals.move-invoices-status.countDescription', {
        count: payload.invoices.length,
        status
      })}
      proposalId={proposalId}
      rows={payload.invoices}
      rowKey={row => row.invoiceId}
      primary={row => (
        <>
          <span className='font-medium'>{row.invoiceNumber}</span>
          {row.supplier && <span className='text-foreground-muted'>{` · ${row.supplier}`}</span>}
        </>
      )}
      secondary={row => formatAmount(row.amount, row.currency) ?? '—'}
      footer={
        totals.length > 0 ? (
          <div className='flex flex-col gap-2xs'>
            {totals.map(total => (
              <div key={total.currency} className='flex items-baseline justify-between gap-sm'>
                <span className='text-foreground-muted text-sm'>{t('proposals.total')}</span>
                <span className='font-semibold text-foreground-base'>{total.label}</span>
              </div>
            ))}
          </div>
        ) : undefined
      }
      resolved={resolved}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
